'use client'

import Link from 'next/link'
import { usePathname, useRouter } from 'next/navigation'
import { useState, useEffect } from 'react'
import { useTheme } from 'next-themes'
import {
  FileText,
  Home,
  Menu,
  X,
  LogOut,
  Bot,
  Lightbulb,
  Share2,
  Tag,
  Tags,
  Sun,
  Moon,
  Monitor,
} from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'
import { btn } from '@/lib/theme'

type NavItem = {
  href: string
  label: string
  icon: React.ComponentType<{ className?: string }>
}

export default function Header() {
  const pathname = usePathname()
  const router = useRouter()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [mounted, setMounted] = useState(false)
  const { isAuthenticated, logout } = useAuthStore()
  const { theme, setTheme } = useTheme()

  useEffect(() => { setMounted(true) }, [])

  const handleLogout = async () => {
    try {
      await logout()
      router.push('/login')
    } catch (error) {
      console.error('Logout failed:', error)
    }
  }

  const isActive = (path: string) => {
    if (path === '/') return pathname === '/'
    return pathname?.startsWith(path) ?? false
  }

  const cycleTheme = () => {
    if (theme === 'light') setTheme('dark')
    else if (theme === 'dark') setTheme('system')
    else setTheme('light')
  }

  const ThemeIcon = !mounted ? Monitor : theme === 'dark' ? Moon : theme === 'light' ? Sun : Monitor
  const themeLabel = !mounted ? 'システム' : theme === 'dark' ? 'ダーク' : theme === 'light' ? 'ライト' : 'システム'

  const navItems: NavItem[] = [
    { href: '/', label: 'ホーム', icon: Home },
    { href: '/articles', label: '記事一覧', icon: FileText },
    { href: '/publications', label: '投稿管理', icon: Share2 },
    { href: '/agent-workflow', label: 'エージェント', icon: Bot },
    { href: '/ideas', label: 'ネタ管理', icon: Lightbulb },
    { href: '/categories', label: 'カテゴリ管理', icon: Tag },
    { href: '/tags', label: 'タグ管理', icon: Tags },
  ]

  return (
    <header className="bg-white dark:bg-gray-800 shadow-md border-b border-gray-200 dark:border-gray-700 sticky top-0 z-50">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* ロゴ・タイトル */}
          <div className="flex items-center space-x-2 min-w-0">
            <Link href="/" className="flex items-center space-x-2 hover:opacity-80 transition-opacity">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <FileText className="h-5 w-5 text-white" />
              </div>
              <div className="hidden sm:block min-w-0">
                <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100 truncate">記事管理システム</h1>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">Claude Code Article Manager</p>
              </div>
              <div className="sm:hidden">
                <h1 className="text-lg font-bold text-gray-900 dark:text-gray-100">記事管理</h1>
              </div>
            </Link>
          </div>

          {/* デスクトップナビゲーション */}
          {isAuthenticated && pathname !== '/login' && (
            <nav className="hidden lg:flex items-center space-x-1">
              {navItems.map((item) => {
                const Icon = item.icon
                return (
                  <Link
                    key={item.href}
                    href={item.href}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isActive(item.href)
                        ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-700'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    <span>{item.label}</span>
                  </Link>
                )
              })}

              {/* ダークモード切り替えボタン */}
              <button
                onClick={cycleTheme}
                className="p-2 rounded-lg transition-colors text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700"
                title={`現在: ${themeLabel}モード（クリックで切り替え）`}
                aria-label={`テーマを切り替え（現在: ${themeLabel}）`}
              >
                <ThemeIcon className="h-4 w-4" />
              </button>

              {/* ログアウトボタン */}
              <button
                onClick={handleLogout}
                className={`${btn.danger} space-x-2`}
                title="ログアウト"
              >
                <LogOut className="h-4 w-4" />
                <span>ログアウト</span>
              </button>
            </nav>
          )}

          {/* タブレット用簡略ナビゲーション */}
          {isAuthenticated && pathname !== '/login' && (
            <nav className="hidden md:flex lg:hidden items-center space-x-1">
              {navItems.map((item) => {
                const Icon = item.icon
                return (
                  <Link
                    key={item.href}
                    href={item.href}
                    className={`flex items-center justify-center w-10 h-10 rounded-lg text-sm font-medium transition-colors ${
                      isActive(item.href)
                        ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-700'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                    title={item.label}
                  >
                    <Icon className="h-5 w-5" />
                  </Link>
                )
              })}

              {/* ダークモード切り替えボタン */}
              <button
                onClick={cycleTheme}
                className="flex items-center justify-center w-10 h-10 rounded-lg transition-colors text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700"
                title={`現在: ${themeLabel}モード`}
                aria-label={`テーマを切り替え（現在: ${themeLabel}）`}
              >
                <ThemeIcon className="h-5 w-5" />
              </button>

              {/* ログアウトボタン */}
              <button
                onClick={handleLogout}
                className={`${btn.icon} text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30 w-10 h-10`}
                title="ログアウト"
              >
                <LogOut className="h-5 w-5" />
              </button>
            </nav>
          )}

          {/* モバイルハンバーガーメニューボタン */}
          {isAuthenticated && pathname !== '/login' && (
            <div className="md:hidden flex items-center gap-1">
              {/* ダークモード切り替えボタン（モバイル） */}
              <button
                onClick={cycleTheme}
                className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                aria-label={`テーマを切り替え（現在: ${themeLabel}）`}
              >
                <ThemeIcon className="h-5 w-5" />
              </button>
              <button
                className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                aria-label="メニューを開く"
              >
                {isMobileMenuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </button>
            </div>
          )}
        </div>

        {/* モバイルドロップダウンメニュー */}
        {isAuthenticated && pathname !== '/login' && isMobileMenuOpen && (
          <div className="md:hidden border-t border-gray-200 dark:border-gray-700 py-2">
            <nav className="space-y-1">
              {navItems.map((item) => {
                const Icon = item.icon
                return (
                  <Link
                    key={item.href}
                    href={item.href}
                    className={`flex items-center space-x-3 px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                      isActive(item.href)
                        ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-700'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <Icon className="h-5 w-5" />
                    <span>{item.label}</span>
                  </Link>
                )
              })}

              {/* ログアウトボタン */}
              <button
                onClick={() => {
                  handleLogout()
                  setIsMobileMenuOpen(false)
                }}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-base font-medium ${btn.danger}`}
              >
                <LogOut className="h-5 w-5" />
                <span>ログアウト</span>
              </button>
            </nav>
          </div>
        )}
      </div>
    </header>
  )
}
